import { Outlet } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { env } from '@/config';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

/**
 * Layout for the public, unauthenticated QR check-in flow (`/check-in/:restaurantId/:tableId`)
 * — reached by guests scanning a physical table QR code, almost always on a phone. Deliberately minimal
 * and mobile-first: no sidebar/nav chrome from DashboardLayout, no brand aside panel from
 * AuthLayout, just a centered card a guest can fill in on a small screen.
 *
 * The header uses a 3-column grid (spacer / logo / controls) so the logo sits truly centered
 * regardless of how wide the language/theme controls on the right are — a plain
 * `justify-content: space-between` row would center it only when both sides happen to match
 * in width.
 */
export function CheckinLayout() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 1,
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box />
        <Box
          component="img"
          src="/logos/logo-main.svg"
          alt={env.appName}
          sx={{ height: { xs: 40, sm: 48, md: 56 }, width: 'auto', maxWidth: '100%' }}
        />
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifySelf: 'flex-end' }}>
          <LanguageSwitcher />
          <ThemeToggle />
        </Stack>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', p: { xs: 2, sm: 4 } }}>
        <Container maxWidth="sm" disableGutters>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: (theme) =>
                theme.palette.mode === 'light' ? '0 24px 48px -24px rgba(20, 20, 43, 0.18)' : 'none',
            }}
          >
            <Outlet />
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
